import {
  Component,
  ElementRef,
  forwardRef,
  inject,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';

import { AvailableLanguages } from '@constants';
import { ConsumableCountry } from '@interfaces';

interface PhoneCountryOption {
  id: number;
  alpha2: string;
  callingCode: string;
  countryLabel: string;
  formattedCallingCode: string;
  searchLabel: string;
}

type PhoneInputMode = 'country-select' | 'manual-calling-code';

interface ManualPhoneParts {
  callingCode: string;
  localDigits: string;
}

const MIN_PHONE_NUMBER_LENGTH = 8;
const MAX_PHONE_NUMBER_LENGTH = 15;
const MAX_MANUAL_CALLING_CODE_LENGTH = 3;
const DEFAULT_MANUAL_PASTE_CALLING_CODE_LENGTH = 2;
const SEQUENTIAL_RUN_LIMIT = 6;
const REPEATING_RUN_LIMIT = 6;
const COMMON_FAKE_LOCAL_NUMBERS = new Set([
  '0000',
  '000000',
  '0000000',
  '00000000',
  '000000000',
  '0000000000',
  '1111',
  '111111',
  '1111111',
  '11111111',
  '111111111',
  '1111111111',
  '1234',
  '12345',
  '123456',
  '1234567',
  '12345678',
  '123456789',
  '23456789',
  '34567890',
  '4321',
  '54321',
  '654321',
  '7654321',
  '87654321',
  '987654321',
  '01234567',
  '012345678',
  '0123456789',
  '09876543',
  '098765432',
  '0987654321',
  '12121212',
  '123123123',
  '69696969',
]);

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class PhoneInputComponent implements ControlValueAccessor, Validator, OnChanges, OnDestroy, OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly injector = inject(Injector);

  private readonly destroy$ = new ReplaySubject<boolean>(1);

  @ViewChild('countrySelectComponent') countrySelectComponent?: NzSelectComponent;
  @ViewChild('manualCallingCodeInput') manualCallingCodeInput?: ElementRef<HTMLInputElement>;
  @ViewChild('phoneNumberInput') phoneNumberInput?: ElementRef<HTMLInputElement>;

  @Input() countries: ConsumableCountry[] = [];
  @Input() inputId = 'phoneInput';
  @Input() loading = false;
  @Input() mode: PhoneInputMode = 'country-select';
  @Input() selectPlaceholder = '+00';
  @Input() numberPlaceholder = 'COMMON.LABEL.PHONE_NUMBER';

  public countryOptions: PhoneCountryOption[] = [];
  public manualCallingCode = '';
  public selectedCountryId: number | null = null;
  public selectedCountryOption: PhoneCountryOption | null = null;
  public localPhoneNumber = '';
  public disabled = false;
  public hasErrors = false;
  public ngControl: NgControl | null = null;

  private currentLanguage: AvailableLanguages = 'pl';
  private countrySearchValue = '';
  private isCountrySelectOpen = false;
  private hasExternalValueParseError = false;
  private pendingExternalValue: string | null = null;
  private shouldFocusPhoneNumberAfterCountryClose = false;
  private onChange: (value: string | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private onValidatorChange: () => void = () => undefined;

  constructor() {
    this.translateService.onLangChange
      .pipe(startWith({ lang: this.translateService.currentLang || 'pl' }), takeUntil(this.destroy$))
      .subscribe(({ lang }) => {
        this.currentLanguage = (lang as AvailableLanguages) || 'pl';
        this.rebuildCountryOptions();
      });
  }

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);

    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
      this.ngControl.control?.statusChanges?.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateHasErrors());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.countries) {
      this.rebuildCountryOptions();
    }

    if (changes.mode && !changes.mode.firstChange) {
      this.syncStateFromExternalValue(this.pendingExternalValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  writeValue(value: string | null): void {
    this.pendingExternalValue = value;
    this.syncStateFromExternalValue(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(_: AbstractControl): ValidationErrors | null {
    if (this.hasExternalValueParseError) {
      return { invalidPhoneNumber: true };
    }

    const callingCode = this.getActiveCallingCode();
    const localDigits = this.normalizeDigits(this.localPhoneNumber);

    if (!callingCode && !localDigits.length) {
      return null;
    }

    if (!callingCode || !localDigits.length) {
      return { invalidPhoneNumber: true };
    }

    const fullLength = callingCode.length + localDigits.length;

    if (fullLength < MIN_PHONE_NUMBER_LENGTH) {
      return { tooShort: true };
    }

    if (fullLength > MAX_PHONE_NUMBER_LENGTH) {
      return { tooLong: true };
    }

    if (!this.isPhonePartsValid(callingCode, localDigits)) {
      return { invalidPhoneNumber: true };
    }

    return null;
  }

  public onCountryChange(countryId: number | null): void {
    if (this.loading) {
      return;
    }

    this.selectedCountryId = countryId;
    this.refreshSelectedCountryOption();
    this.countrySearchValue = '';
    this.emitValue();
    if (countryId !== null && !this.shouldFocusPhoneNumberAfterCountryClose) {
      this.focusPhoneNumberInput();
    }
  }

  public onCountryOpenChange(open: boolean): void {
    this.isCountrySelectOpen = open;

    if (!open && this.shouldFocusPhoneNumberAfterCountryClose) {
      this.shouldFocusPhoneNumberAfterCountryClose = false;
      this.focusPhoneNumberInput();
    }
  }

  public onCountrySearch(value: string): void {
    if (this.loading) {
      return;
    }

    this.countrySearchValue = value ?? '';
  }

  public onLocalPhoneNumberChange(value: string): void {
    if (this.loading) {
      return;
    }

    this.localPhoneNumber = this.normalizeDigits(value ?? '');
    this.emitValue();
  }

  public onManualCallingCodeChange(value: string): void {
    if (this.loading) {
      return;
    }

    this.manualCallingCode = this.normalizeManualCallingCode(value ?? '');
    this.emitValue();
  }

  public onCountrySelectKeyDown(event: Event): void {
    if (!(event instanceof KeyboardEvent) || event.defaultPrevented || this.disabled || this.loading) {
      return;
    }

    if (event.key === 'Enter' && this.isCountrySelectOpen) {
      this.shouldFocusPhoneNumberAfterCountryClose = true;
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    const matchedCountry = this.findCountryByCallingCode(this.countrySearchValue);

    if (!matchedCountry) {
      return;
    }

    event.preventDefault();
    this.shouldFocusPhoneNumberAfterCountryClose = false;
    this.applyCountrySelection(matchedCountry.id);
    this.emitValue();
    this.focusPhoneNumberInput();
  }

  public onPhoneNumberKeyDown(event: KeyboardEvent): void {
    if (this.loading) {
      event.preventDefault();
      return;
    }

    if (
      event.ctrlKey ||
      event.metaKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Enter',
        'Escape',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  public onManualCallingCodeKeyDown(event: KeyboardEvent): void {
    if (this.loading) {
      event.preventDefault();
      return;
    }

    if (
      event.ctrlKey ||
      event.metaKey ||
      [
        'Backspace',
        'Delete',
        'Tab',
        'Enter',
        'Escape',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ].includes(event.key)
    ) {
      return;
    }

    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    const input = this.manualCallingCodeInput?.nativeElement;

    if (!input) {
      return;
    }

    const selectionStart = input.selectionStart ?? this.manualCallingCode.length;
    const selectionEnd = input.selectionEnd ?? this.manualCallingCode.length;
    const nextLength = this.manualCallingCode.length - (selectionEnd - selectionStart) + 1;

    if (nextLength > MAX_MANUAL_CALLING_CODE_LENGTH) {
      event.preventDefault();
    }
  }

  public onPhoneNumberPaste(event: ClipboardEvent): void {
    if (this.loading) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') ?? '';

    if (this.mode === 'manual-calling-code' && this.tryApplyManualPhonePaste(pastedText, 'local-number')) {
      return;
    }

    const pastedDigits = this.normalizeDigits(pastedText);

    if (!pastedDigits.length) {
      return;
    }

    this.insertDigitsIntoLocalPhoneNumber(pastedDigits);
  }

  public onManualCallingCodePaste(event: ClipboardEvent): void {
    if (this.loading) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') ?? '';

    if (this.tryApplyManualPhonePaste(pastedText, 'calling-code')) {
      return;
    }

    const nextCallingCode = this.normalizeManualCallingCode(pastedText);

    if (!nextCallingCode.length) {
      return;
    }

    this.manualCallingCode = nextCallingCode;
    this.emitValue();
  }

  public markAsTouched(): void {
    this.onTouched();
    this.updateHasErrors();
  }

  private updateHasErrors(): void {
    const control = this.ngControl?.control;
    this.hasErrors = !!control?.touched && !!control?.invalid;
  }

  private resolveCountryOption(value: unknown): PhoneCountryOption | null {
    return this.countryOptions?.find(option => option.id === value) ?? null;
  }

  private refreshSelectedCountryOption(): void {
    this.selectedCountryOption = this.resolveCountryOption(this.selectedCountryId);
  }

  private rebuildCountryOptions(): void {
    if (this.mode === 'manual-calling-code') {
      this.onValidatorChange();
      return;
    }

    const currentSelectedCountryId = this.selectedCountryId;

    this.countryOptions = (this.countries ?? [])
      .filter(country => !!this.normalizeDigits(country.callingCode))
      .map(country => {
        const countryLabel = country.nameTranslations?.[this.currentLanguage] ?? country.name;
        const callingCode = this.normalizeDigits(country.callingCode);
        const formattedCallingCode = '+' + callingCode;

        return {
          id: country.id,
          alpha2: country.alpha2.toUpperCase(),
          callingCode,
          countryLabel,
          formattedCallingCode,
          searchLabel: `${countryLabel} ${country.alpha2.toUpperCase()} ${formattedCallingCode}`,
        };
      })
      .sort((left, right) => left.countryLabel.localeCompare(right.countryLabel));

    if (currentSelectedCountryId && this.countryOptions.some(option => option.id === currentSelectedCountryId)) {
      this.selectedCountryId = currentSelectedCountryId;
    } else if (this.pendingExternalValue) {
      this.syncStateFromExternalValue(this.pendingExternalValue);
    }

    this.refreshSelectedCountryOption();
    this.onValidatorChange();
  }

  private syncStateFromExternalValue(value: string | null): void {
    if (!value) {
      this.hasExternalValueParseError = false;
      this.resetState();
      this.onValidatorChange();
      return;
    }

    const normalizedDigits = this.normalizeDigits(value);

    if (!normalizedDigits.length) {
      this.hasExternalValueParseError = true;
      this.resetState();
      this.onValidatorChange();
      return;
    }

    if (this.mode === 'manual-calling-code') {
      const manualPhoneParts = this.extractManualPhoneParts(normalizedDigits);

      if (!manualPhoneParts || !this.isPhonePartsValid(manualPhoneParts.callingCode, manualPhoneParts.localDigits)) {
        this.hasExternalValueParseError = true;
        this.resetState();
        this.onValidatorChange();
        return;
      }

      this.hasExternalValueParseError = false;
      this.manualCallingCode = manualPhoneParts.callingCode;
      this.localPhoneNumber = manualPhoneParts.localDigits;
      this.onValidatorChange();
      return;
    }

    if (!this.countryOptions.length) {
      this.resetState();
      this.onValidatorChange();
      return;
    }

    const matchedCountry = [...this.countryOptions]
      .sort((left, right) => right.callingCode.length - left.callingCode.length)
      .find(option => normalizedDigits.startsWith(option.callingCode));

    if (!matchedCountry) {
      this.hasExternalValueParseError = true;
      this.resetState();
      this.onValidatorChange();
      return;
    }

    const localPhoneNumber = normalizedDigits.slice(matchedCountry.callingCode.length);

    if (!this.isPhonePartsValid(matchedCountry.callingCode, localPhoneNumber)) {
      this.hasExternalValueParseError = true;
      this.resetState();
      this.onValidatorChange();
      return;
    }

    this.hasExternalValueParseError = false;
    this.selectedCountryId = matchedCountry.id;
    this.refreshSelectedCountryOption();
    this.localPhoneNumber = localPhoneNumber;
    this.onValidatorChange();
  }

  private emitValue(): void {
    const localDigits = this.normalizeDigits(this.localPhoneNumber);
    const callingCode = this.getActiveCallingCode();
    const normalizedValue = this.shouldEmitPhoneValue(callingCode, localDigits)
      ? this.buildPhoneValue(callingCode, localDigits)
      : null;

    this.hasExternalValueParseError = false;
    this.pendingExternalValue = normalizedValue;
    this.onChange(normalizedValue);
    this.onValidatorChange();
  }

  private findCountryByCallingCode(value: string): PhoneCountryOption | null {
    const normalizedValue = this.normalizeDigits(value);

    if (!normalizedValue.length) {
      return null;
    }

    return this.countryOptions.find(option => option.callingCode === normalizedValue) ?? null;
  }

  private applyCountrySelection(countryId: number): void {
    this.selectedCountryId = countryId;
    this.refreshSelectedCountryOption();
    this.countrySearchValue = '';
    this.countrySelectComponent?.writeValue(countryId);
    this.countrySelectComponent?.setOpenState(false);
  }

  private shouldEmitPhoneValue(callingCode: string, localDigits: string): boolean {
    if (!callingCode.length && !localDigits.length) {
      return false;
    }

    if (this.mode === 'manual-calling-code') {
      return this.isPhonePartsValid(callingCode, localDigits);
    }

    return !!callingCode.length || !!localDigits.length;
  }

  private getActiveCallingCode(): string {
    if (this.mode === 'manual-calling-code') {
      return this.manualCallingCode;
    }

    return this.selectedCountryOption?.callingCode ?? '';
  }

  private isPhonePartsValid(callingCode: string, localDigits: string): boolean {
    if (!callingCode.length || !localDigits.length) {
      return false;
    }

    const fullLength = callingCode.length + localDigits.length;

    if (fullLength < MIN_PHONE_NUMBER_LENGTH || fullLength > MAX_PHONE_NUMBER_LENGTH) {
      return false;
    }

    if (localDigits.startsWith('0')) {
      return false;
    }

    if (COMMON_FAKE_LOCAL_NUMBERS.has(localDigits)) {
      return false;
    }

    if (/^(\d)\1+$/.test(localDigits)) {
      return false;
    }

    if (this.hasRepeatingRun(localDigits, REPEATING_RUN_LIMIT)) {
      return false;
    }

    if (this.hasSequentialRun(localDigits, SEQUENTIAL_RUN_LIMIT)) {
      return false;
    }

    if (this.isRepeatedBlockPattern(localDigits)) {
      return false;
    }

    return true;
  }

  private hasRepeatingRun(value: string, minRunLength: number): boolean {
    if (value.length < minRunLength) {
      return false;
    }

    let currentRunLength = 1;

    for (let index = 1; index < value.length; index += 1) {
      if (value[index] === value[index - 1]) {
        currentRunLength += 1;
        if (currentRunLength >= minRunLength) {
          return true;
        }
        continue;
      }

      currentRunLength = 1;
    }

    return false;
  }

  private hasSequentialRun(value: string, minRunLength: number): boolean {
    if (value.length < minRunLength) {
      return false;
    }

    let ascendingRunLength = 1;
    let descendingRunLength = 1;

    for (let index = 1; index < value.length; index += 1) {
      const previousDigit = Number(value[index - 1]);
      const currentDigit = Number(value[index]);

      ascendingRunLength = currentDigit === previousDigit + 1 ? ascendingRunLength + 1 : 1;
      descendingRunLength = currentDigit === previousDigit - 1 ? descendingRunLength + 1 : 1;

      if (ascendingRunLength >= minRunLength || descendingRunLength >= minRunLength) {
        return true;
      }
    }

    return false;
  }

  private isRepeatedBlockPattern(value: string): boolean {
    if (value.length < 6) {
      return false;
    }

    const maxBlockLength = Math.min(4, Math.floor(value.length / 2));

    for (let blockLength = 2; blockLength <= maxBlockLength; blockLength += 1) {
      if (value.length % blockLength !== 0) {
        continue;
      }

      const block = value.slice(0, blockLength);
      if (block.repeat(value.length / blockLength) === value) {
        return true;
      }
    }

    return false;
  }

  private resetState(): void {
    this.manualCallingCode = '';
    this.selectedCountryId = null;
    this.selectedCountryOption = null;
    this.localPhoneNumber = '';
  }

  private buildPhoneValue(callingCode?: string, localDigits?: string): string | null {
    const normalizedCallingCode = this.normalizeDigits(callingCode ?? '');
    const normalizedLocalDigits = this.normalizeDigits(localDigits ?? '');

    if (!normalizedCallingCode && !normalizedLocalDigits) {
      return null;
    }

    return `+${normalizedCallingCode}${normalizedLocalDigits}`;
  }

  private normalizeDigits(value: string): string {
    return (value ?? '').replace(/\D+/g, '');
  }

  private normalizeManualCallingCode(value: string): string {
    return this.normalizeDigits(value).replace(/^0+/, '').slice(0, MAX_MANUAL_CALLING_CODE_LENGTH);
  }

  private extractManualPhoneParts(value: string): ManualPhoneParts | null {
    const normalizedDigits = this.normalizeDigits(value).replace(/^0+/, '');

    if (normalizedDigits.length <= DEFAULT_MANUAL_PASTE_CALLING_CODE_LENGTH) {
      return null;
    }

    const callingCode = this.normalizeManualCallingCode(
      normalizedDigits.slice(0, DEFAULT_MANUAL_PASTE_CALLING_CODE_LENGTH)
    );
    const localDigits = this.normalizeDigits(normalizedDigits.slice(DEFAULT_MANUAL_PASTE_CALLING_CODE_LENGTH));

    if (!callingCode.length || !localDigits.length) {
      return null;
    }

    return { callingCode, localDigits };
  }

  private tryApplyManualPhonePaste(pastedText: string, source: 'calling-code' | 'local-number'): boolean {
    const pastedDigits = this.normalizeDigits(pastedText);

    if (!pastedDigits.length || !this.shouldAutoSplitManualPaste(pastedText, pastedDigits, source)) {
      return false;
    }

    const manualPhoneParts = this.extractManualPhoneParts(pastedText);

    if (!manualPhoneParts) {
      return false;
    }

    this.manualCallingCode = manualPhoneParts.callingCode;
    this.localPhoneNumber = manualPhoneParts.localDigits;
    this.emitValue();

    setTimeout(() => {
      const nextCaretPosition = this.localPhoneNumber.length;
      this.phoneNumberInput?.nativeElement.setSelectionRange(nextCaretPosition, nextCaretPosition);
      this.phoneNumberInput?.nativeElement.focus();
    });

    return true;
  }

  private shouldAutoSplitManualPaste(
    pastedText: string,
    pastedDigits: string,
    source: 'calling-code' | 'local-number'
  ): boolean {
    const trimmedValue = pastedText.trim();
    const startsWithInternationalPrefix = trimmedValue.startsWith('+') || trimmedValue.startsWith('00');
    const containsSeparators = /[\s\-()]/.test(trimmedValue);

    if (startsWithInternationalPrefix || containsSeparators) {
      return pastedDigits.length > DEFAULT_MANUAL_PASTE_CALLING_CODE_LENGTH;
    }

    if (source === 'calling-code') {
      return pastedDigits.length > MAX_MANUAL_CALLING_CODE_LENGTH;
    }

    return (
      !this.manualCallingCode.length && !this.localPhoneNumber.length && pastedDigits.length >= MIN_PHONE_NUMBER_LENGTH
    );
  }

  private insertDigitsIntoLocalPhoneNumber(pastedDigits: string): void {
    const input = this.phoneNumberInput?.nativeElement;

    if (!input) {
      this.onLocalPhoneNumberChange(this.localPhoneNumber + pastedDigits);
      return;
    }

    const selectionStart = input.selectionStart ?? this.localPhoneNumber.length;
    const selectionEnd = input.selectionEnd ?? this.localPhoneNumber.length;
    const nextValue =
      this.localPhoneNumber.slice(0, selectionStart) + pastedDigits + this.localPhoneNumber.slice(selectionEnd);

    this.onLocalPhoneNumberChange(nextValue);

    setTimeout(() => {
      const nextCaretPosition = selectionStart + pastedDigits.length;
      input.setSelectionRange(nextCaretPosition, nextCaretPosition);
    });
  }

  private focusPhoneNumberInput(): void {
    setTimeout(() => this.phoneNumberInput?.nativeElement.focus());
  }
}
